"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.claimAndDepositInstruction = void 0;
const api_1 = require("./api");
const solana_spl_token_modern_1 = require("solana-spl-token-modern");
const vsr_client_1 = require("./vsr-client");
/**
 * Generate instruction to claim from the ClaimRecord.
 * This function loads data from blockchain when not all parameters are provided.
 *
 * @type {Object} args - Arguments on instruction creation
 * @param param {IncentivesDistributionProgram} args.program - anchor program instance
 * @param param {PublicKey} args.treasuryAccount - treasury account
 * @param param {PublicKey} args.tokenStore - token store (when not provided, it will be loaded from treasuryAccount)
 * @param param {PublicKey} args.tokenMint - token mint that's used to generate address for VSR vault (when not provided, it will be loaded from tokenStore)
 * @param param {PublicKey} args.claimRecordAccount - claim record account (when not provided, it will be calculated from claimRecordAuthority)
 * @param param {PublicKey} args.claimRecordAuthority - claim record authority [SIGNER] (loaded from claimRecordAccount when not provided)
 * @param param {number} args.depositEntryIndex - deposit entry index to store locked tokens into VSR
 * @type {Object} return - Return data of generated instruction
 * @return {TransactionInstruction} return.instruction - Instruction to claim ClaimRecord
 * @return {PublicKey} return.address - claim record account address
 */
async function claimAndDepositInstruction({ program, treasuryAccount, tokenStore, tokenMint, claimRecordAccount, claimRecordAuthority = program.provider.publicKey, depositEntryIndex, }) {
    if (!claimRecordAccount && !claimRecordAuthority) {
        throw new Error('Either claimRecordAccount or claimRecordAuthority must be provided');
    }
    if (!claimRecordAccount && treasuryAccount) {
        const { address } = (0, api_1.claimRecordAddress)({
            programId: program.programId,
            treasury: treasuryAccount,
            authority: claimRecordAuthority,
        });
        claimRecordAccount = address;
    }
    if (claimRecordAccount && (!claimRecordAuthority || !treasuryAccount)) {
        const claimRecordData = await (0, api_1.claimRecord)({
            program,
            address: claimRecordAccount,
        });
        claimRecordAuthority = claimRecordData.authority;
        treasuryAccount = claimRecordData.treasuryAccount;
    }
    if (!claimRecordAccount) {
        throw new Error('Cannot get claim record account address, provide claim-record-authority and treasury-account');
    }
    if (!treasuryAccount) {
        throw new Error('Cannot get treasury account address, provide claim-record or the treasury-account');
    }
    const { address: tokenAuthority } = (0, api_1.treasuryTokenAuthorityAddress)({
        programId: program.programId,
        treasury: treasuryAccount,
    });
    if (!tokenStore) {
        const treasuryData = await (0, api_1.treasury)({ program, address: treasuryAccount });
        tokenStore = treasuryData.tokenStore;
    }
    if (!tokenMint) {
        const tokenStoreData = await (0, solana_spl_token_modern_1.getAccount)(program.provider.connection, tokenStore);
        tokenMint = tokenStoreData.mint;
    }
    const [vsrVoter] = (0, vsr_client_1.voterAddress)({
        programId: vsr_client_1.MNDE_VSR_PROGRAM_ID,
        registrar: vsr_client_1.MNDE_VSR_REGISTRAR_ADDRESS,
        voterAuthority: claimRecordAuthority,
    });
    const instruction = await program.methods
        .claimAndDeposit(depositEntryIndex)
        .accountsStrict({
        treasuryAccount,
        claimRecordAccount,
        tokenAuthority,
        authority: claimRecordAuthority,
        tokenStore,
        tokenProgram: solana_spl_token_modern_1.TOKEN_PROGRAM_ID,
        vsrProgram: vsr_client_1.MNDE_VSR_PROGRAM_ID,
        vsrRegistrar: vsr_client_1.MNDE_VSR_REGISTRAR_ADDRESS,
        vsrVoter,
        vsrVault: (0, vsr_client_1.vaultAddress)({ voter: vsrVoter, mint: tokenMint }),
    })
        .instruction();
    return { address: claimRecordAccount, instruction };
}
exports.claimAndDepositInstruction = claimAndDepositInstruction;
//# sourceMappingURL=claimAndDepositInstruction.js.map